/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Booking detail — /real-estate/bookings/:id
 *
 * GET/PUT /re/bookings/{id}; POST /status, /cancel, /regenerate-schedule;
 * POST /installments/{iid}/payments and /installments/{iid}/invoice;
 * GET/POST /re/resales?booking_id=; POST /re/commissions/recalculate/{booking_id}.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, Link, MenuItem, Skeleton, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import UserLink from 'components/v2/UserLink';
import { usePermission } from 'auth/usePermission';
import RePage, { LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import ReStepper from './components/ReStepper';
import { ContactPicker, UserPicker } from './components/Pickers';
import { DateField, KV, Num, ProgressBar } from './components/Widgets';
import { triggerText } from './components/SchedulePreview';
import {
  PAYMENT_MODES, bedroomsLabel, contactLabel, errorMessage, formatDate, formatMoney, formatPct, humanize,
  round2, todayIso, toNumber, useReApi, useReGet, useReList,
} from './api';
import { bookingFlowCodes, bookingStatusLabels, defaultCurrency, isAustralia } from '../variant';
import useReVariant from '../useReVariant';

const NEXT_ACTION_LABEL = {
  RESERVED: 'Issue SPA',
  SPA_ISSUED: 'Mark SPA signed',
  SPA_SIGNED: 'Mark Oqood registered',
  REGISTERED: 'Record handover',
  DRAFT: 'Reserve',
};

// Australia: contract of sale (variants/au.py).
const NEXT_ACTION_LABEL_AU = {
  DRAFT: 'Take holding deposit',
  RESERVED: 'Mark contracts exchanged',
  CONTRACT_EXCHANGED: 'Mark unconditional',
  UNCONDITIONAL: 'Record settlement',
};

function PaymentDialog({ open, booking, installment, onClose, onDone }) {
  const { api } = useReApi();
  const outstanding = installment ? round2((toNumber(installment.amount) || 0) - (toNumber(installment.amount_paid) || 0)) : 0;
  const [form, setForm] = useState({ amount: '', paid_at: todayIso(), reference: '', payment_mode: 'BANK_TRANSFER' });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({ amount: String(outstanding), paid_at: todayIso(), reference: '', payment_mode: 'BANK_TRANSFER' });
      setErr(null);
    }
  }, [open, outstanding]);

  const submit = async () => {
    const amount = toNumber(form.amount);
    if (!amount || amount <= 0) {
      setErr('Enter an amount greater than zero.');
      return;
    }
    if (amount > outstanding + 0.005) {
      setErr(`That is more than the ${formatMoney(outstanding, booking.currency)} outstanding.`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/re/bookings/${booking.id}/installments/${installment.id}/payments`, {
        amount, paid_at: form.paid_at, reference: form.reference || null, payment_mode: form.payment_mode,
      });
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not record the payment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`Record payment · ${installment?.label || ''}`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDBox mb={2}>
          <KV label="Installment" value={formatMoney(installment?.amount, booking?.currency)} />
          <KV label="Outstanding" value={formatMoney(outstanding, booking?.currency)} bold />
        </MDBox>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <TextField
            size="small" type="number" label="Amount" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">{booking?.currency || defaultCurrency()}</InputAdornment> }}
          />
          <DateField label="Received on" value={form.paid_at} onChange={(v) => setForm({ ...form, paid_at: v })} />
          <TextField select size="small" label="Mode" value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}>
            {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{humanize(m)}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Reference (cheque / transfer no.)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="success" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Record payment'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function CancelDialog({ open, booking, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ reason: '', effective_date: todayIso(), forfeit_pct: '' });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const paid = toNumber(booking?.amount_paid) || 0;
  const net = toNumber(booking?.net_price) || 0;
  const pct = toNumber(form.forfeit_pct) || 0;
  // Estimate: forfeit is a % of the net price, never more than what was paid.
  const forfeit = round2(Math.min(paid, (net * pct) / 100));
  const refund = round2(paid - forfeit);

  const submit = async () => {
    if (!form.reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    if (pct < 0 || pct > 100) {
      setErr('Forfeit % must be between 0 and 100.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/re/bookings/${booking.id}/cancel`, {
        reason: form.reason, effective_date: form.effective_date, forfeit_pct: form.forfeit_pct === '' ? null : pct,
      });
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not cancel the booking'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{`Cancel booking ${booking?.booking_number || ''}`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          The unit returns to AVAILABLE, unpaid installments are cancelled, paid broker commissions are clawed back and the rest cancelled.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Grid>
          <Grid item xs={6}>
            <DateField label="Effective date" value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} sx={{ width: '100%' }} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" type="number" label="Forfeit" value={form.forfeit_pct}
              onChange={(e) => setForm({ ...form, forfeit_pct: e.target.value })}
              InputProps={{ endAdornment: <InputAdornment position="end">% of net price</InputAdornment> }}
            />
          </Grid>
        </Grid>
        <RePanel sx={{ mt: 2, bgcolor: '#FAFBFD' }}>
          <KV label="Net price" value={formatMoney(net, booking?.currency)} />
          <KV label="Paid by buyer" value={formatMoney(paid, booking?.currency)} />
          <KV label={`Forfeited (${formatPct(pct, 2)}, capped at paid)`} value={`− ${formatMoney(forfeit, booking?.currency)}`} color="#BA1A1A" />
          <KV label="Refund to buyer" value={formatMoney(refund, booking?.currency)} bold />
          <MDTypography variant="caption" color="text">Estimate. The backend calculates the final forfeit and refund.</MDTypography>
        </RePanel>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Keep booking</MDButton>
        <MDButton variant="gradient" color="error" onClick={submit} disabled={saving}>{saving ? 'Cancelling…' : 'Cancel booking'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function ResaleDialog({ open, booking, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ buyer_contact_id: null, resale_price: '', transfer_fee_amount: '' });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.buyer_contact_id || !toNumber(form.resale_price)) {
      setErr('Choose the new buyer and enter the resale price.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/re/resales', {
        booking_id: booking.id,
        buyer_contact_id: form.buyer_contact_id,
        resale_price: toNumber(form.resale_price),
        transfer_fee_amount: toNumber(form.transfer_fee_amount),
      });
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not start the resale'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Resale / assignment</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDTypography variant="button" color="text" component="p" mb={2}>
          Records the transfer of this booking to a new buyer. The developer NOC and transfer are tracked on the resale.
        </MDTypography>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <ContactPicker label="New buyer" value={form.buyer_contact_id} onChange={(v) => setForm({ ...form, buyer_contact_id: v })} allowCreate sx={{ width: '100%' }} />
          <TextField size="small" type="number" label={`Resale price (${booking?.currency || defaultCurrency()})`} value={form.resale_price} onChange={(e) => setForm({ ...form, resale_price: e.target.value })} />
          <TextField size="small" type="number" label="Developer transfer fee" value={form.transfer_fee_amount} onChange={(e) => setForm({ ...form, transfer_fee_amount: e.target.value })} />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Close</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>Start resale</MDButton>
      </DialogActions>
    </Dialog>
  );
}

const REG_FIELDS = [
  { key: 'spa_issued_date', label: 'SPA issued', type: 'date' },
  { key: 'spa_signed_date', label: 'SPA signed', type: 'date' },
  { key: 'oqood_no', label: 'Oqood no.' },
  { key: 'oqood_registered_date', label: 'Oqood registered', type: 'date' },
  { key: 'handover_date', label: 'Handover date', type: 'date' },
  { key: 'title_deed_no', label: 'Title deed no.' },
];

const REG_FIELDS_AU = [
  { key: 'contract_exchanged_date', label: 'Contracts exchanged', type: 'date' },
  { key: 'cooling_off_ends', label: 'Cooling-off ends', type: 'date' },
  { key: 'finance_approval_date', label: 'Finance approved', type: 'date' },
  { key: 'settlement_date', label: 'Settlement', type: 'date' },
  { key: 'sunset_date', label: 'Sunset date (off the plan)', type: 'date' },
  { key: 'deposit_pct', label: 'Deposit %' },
  { key: 'deposit_held_by', label: 'Deposit held by (trust account)' },
  { key: 'transfer_duty_estimate', label: 'Transfer duty (estimate)' },
];

const regFields = () => (isAustralia() ? REG_FIELDS_AU : REG_FIELDS);

function RegistrationDialog({ open, booking, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  React.useEffect(() => {
    if (open && booking) {
      const f = { assigned_to: booking.assigned_to ?? null };
      regFields().forEach((x) => { f[x.key] = booking[x.key] ?? ''; });
      setForm(f);
      setErr(null);
    }
  }, [open, booking]);
  const submit = async () => {
    setSaving(true);
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      const before = booking[k] ?? (k === 'assigned_to' ? null : '');
      if (v !== before) body[k] = v === '' ? null : v;
    });
    try {
      if (Object.keys(body).length) await api.put(`/re/bookings/${booking.id}`, body);
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isAustralia() ? 'Contract of sale' : <>SPA, Oqood &amp; title deed</>}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {regFields().map((f) => (
            <Grid item xs={12} sm={6} key={f.key}>
              {f.type === 'date' ? (
                <DateField label={f.label} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v || '' })} sx={{ width: '100%' }} />
              ) : (
                <TextField fullWidth size="small" label={f.label} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              )}
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <UserPicker label="Assigned to" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })} emptyLabel="Unassigned" sx={{ width: '100%' }} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>Save</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function Notes({ booking, canEdit, onSaved, notify }) {
  const { api } = useReApi();
  const [text, setText] = useState(booking.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = (booking.notes || '') !== text;
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/re/bookings/${booking.id}`, { notes: text });
      notify('Notes saved');
      onSaved();
    } catch (e) {
      notify.error(e, 'Could not save notes');
    } finally {
      setSaving(false);
    }
  };
  return (
    <InfoCard icon="sticky_note_2" title="Notes" headerAction={canEdit && dirty ? <MDButton size="small" variant="text" color="info" onClick={save} disabled={saving}>Save</MDButton> : null}>
      <TextField fullWidth multiline minRows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={!canEdit} placeholder="Internal notes about this booking" />
    </InfoCard>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canEdit } = usePermission('re_bookings');
  const { canEdit: canEditCommission } = usePermission('re_commission_entries');
  const { data: b, loading, error, reload } = useReGet(`/re/bookings/${id}`);
  const resales = useReList('/re/resales', { booking_id: id });
  const [payFor, setPayFor] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resaleOpen, setResaleOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const pack = useReVariant();
  const flow = bookingFlowCodes(pack);
  const au = isAustralia(pack);
  const flowLabel = (code) => (au ? bookingStatusLabels(pack)[code] || humanize(code) : humanize(code));
  const flowIdx = b ? flow.indexOf(b.status) : -1;
  const nextStatus = flowIdx >= 0 && flowIdx < flow.length - 1 ? flow[flowIdx + 1] : null;
  const installments = useMemo(() => [...(b?.installments || [])].sort((x, y) => x.sequence - y.sequence), [b]);
  const invoiceById = useMemo(() => {
    const m = {};
    (b?.invoices || []).forEach((inv) => { m[inv.id] = inv; });
    return m;
  }, [b]);
  const anyPaid = installments.some((i) => (toNumber(i.amount_paid) || 0) > 0);
  const cur = b?.currency || defaultCurrency();

  const advance = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Move booking to ${humanize(nextStatus)}?`)) return;
    setBusy(true);
    try {
      await api.post(`/re/bookings/${id}/status`, { status: nextStatus });
      notify(`Booking is now ${humanize(nextStatus)}`);
      reload();
    } catch (e) {
      notify.error(e, 'Could not change the status');
    } finally {
      setBusy(false);
    }
  };

  const createInvoice = async (inst) => {
    setBusy(true);
    try {
      const res = await api.post(`/re/bookings/${id}/installments/${inst.id}/invoice`);
      notify(`Invoice ${res.data?.invoice_number || res.data?.id || ''} created`);
      reload();
      if (res.data?.id) navigate(`/invoices/${res.data.id}`);
    } catch (e) {
      notify.error(e, 'Could not create the invoice');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Rebuild the installment schedule from the payment plan?')) return;
    setBusy(true);
    try {
      await api.post(`/re/bookings/${id}/regenerate-schedule`);
      notify('Schedule regenerated');
      reload();
    } catch (e) {
      notify.error(e, 'Could not regenerate the schedule');
    } finally {
      setBusy(false);
    }
  };

  const recalc = async () => {
    setBusy(true);
    try {
      await api.post(`/re/commissions/recalculate/${id}`);
      notify('Commissions recalculated');
      reload();
    } catch (e) {
      notify.error(e, 'Could not recalculate commissions');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !b) return <RePage title="Booking" onBack={() => navigate('/real-estate/bookings')}><Skeleton variant="rectangular" height={300} /></RePage>;
  if (!b) return <RePage title="Booking" onBack={() => navigate('/real-estate/bookings')}><LoadError error={error} onRetry={reload} /></RePage>;

  const cancelled = b.status === 'CANCELLED';
  const scheduleTotals = installments.reduce((a, i) => ({
    amount: a.amount + (i.status === 'CANCELLED' ? 0 : toNumber(i.amount) || 0),
    paid: a.paid + (toNumber(i.amount_paid) || 0),
  }), { amount: 0, paid: 0 });

  return (
    <RePage
      title={b.booking_number || `Booking #${b.id}`}
      subtitle={`${b.project?.name || b.project_name || ''} · Unit ${b.unit?.unit_number || b.unit_number || ''} · booked ${formatDate(b.booking_date)}`}
      onBack={() => navigate('/real-estate/bookings')}
      actions={(
        <>
          <ReStatusChip vocab="booking" status={b.status} />
          {canEdit && nextStatus && (
            <MDButton variant="gradient" color="info" size="small" onClick={advance} disabled={busy}>
              {(au ? NEXT_ACTION_LABEL_AU : NEXT_ACTION_LABEL)[b.status] || `Move to ${flowLabel(nextStatus)}`}
            </MDButton>
          )}
          {canEdit && !cancelled && (
            <MDButton variant="outlined" color="dark" size="small" onClick={() => setRegOpen(true)}>{au ? 'Contract dates' : 'SPA / Oqood'}</MDButton>
          )}
          {canEdit && !cancelled && (au ? ['SETTLED'] : ['SPA_SIGNED', 'REGISTERED', 'HANDED_OVER']).includes(b.status) && (
            <MDButton variant="outlined" color="info" size="small" onClick={() => setResaleOpen(true)}>Resale</MDButton>
          )}
          {canEdit && !cancelled && !(pack.booking_final_statuses || ['HANDED_OVER']).includes(b.status) && (
            <MDButton variant="outlined" color="error" size="small" onClick={() => setCancelOpen(true)}>Cancel</MDButton>
          )}
        </>
      )}
    >
      <RePanel sx={{ mb: 2 }}>
        {cancelled ? (
          <Alert severity="error">
            {`Cancelled${b.cancellation?.effective_date ? ` on ${formatDate(b.cancellation.effective_date)}` : ''}${b.cancellation?.reason ? `: ${b.cancellation.reason}` : ''}. `}
            {b.cancellation && `Forfeited ${formatMoney(b.cancellation.forfeit_amount, cur)}, refund ${formatMoney(b.cancellation.refund_amount, cur)} (${humanize(b.cancellation.refund_status) || 'pending'}).`}
          </Alert>
        ) : (
          <ReStepper steps={flow.map(flowLabel)} activeStep={flowIdx} />
        )}
      </RePanel>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <InfoCard icon="groups" title="Parties">
            {(b.parties || []).map((p) => (
              <MDBox key={p.id || `${p.contact_id}-${p.role}`} mb={1.25}>
                <MDBox display="flex" justifyContent="space-between">
                  <Link component="button" variant="button" onClick={() => navigate(`/contacts/${p.contact_id}`)} sx={{ fontWeight: 600 }}>
                    {p.contact_name || contactLabel(p.contact) || `Contact #${p.contact_id}`}
                  </Link>
                  <MDTypography variant="caption" color="text">
                    {humanize(p.role)}{p.ownership_pct != null ? ` · ${formatPct(p.ownership_pct, 0)}` : ''}
                  </MDTypography>
                </MDBox>
                <MDTypography variant="caption" color="text" component="div">
                  {[p.contact_email || p.contact?.email, p.contact_phone || p.contact?.phone, p.nationality, p.emirates_id && `EID ${p.emirates_id}`, p.passport_no && `PP ${p.passport_no}`].filter(Boolean).join(' · ')}
                </MDTypography>
              </MDBox>
            ))}
            <KV label="Broker" value={b.broker_firm_name ? `${b.broker_firm_name}${b.broker_agent_name ? ` · ${b.broker_agent_name}` : ''}` : 'Direct'} />
            <KV label="Assigned to" value={b.assigned_to_name ? <UserLink userId={b.assigned_to} name={b.assigned_to_name} tone="link" /> : '—'} />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="apartment" title="Unit & price">
            <KV label="Unit" value={[b.unit?.unit_number || b.unit_number, b.unit?.building_name].filter(Boolean).join(' · ')} />
            <KV label="Type" value={[bedroomsLabel(b.unit?.bedrooms), humanize(b.unit?.category)].filter((x) => x && x !== '—').join(' · ') || '—'} />
            <KV label="List price" value={formatMoney(b.list_price, cur)} />
            <KV label="Discount" value={formatMoney(b.discount_amount, cur)} />
            <KV label="Net price" value={formatMoney(b.net_price, cur)} bold />
            {au ? (
              <>
                <KV label={`Deposit ${formatPct(b.deposit_pct, 0)}`} value={formatMoney(round2(((toNumber(b.net_price) || 0) * (toNumber(b.deposit_pct) || 0)) / 100), cur)} />
                <KV label="Transfer duty (estimate)" value={formatMoney(b.transfer_duty_estimate, cur)} />
              </>
            ) : (
              <>
                <KV label={`DLD fee ${formatPct(b.dld_fee_pct, 0)}${b.dld_fee_waived ? ' (developer)' : ''}`} value={formatMoney(b.dld_fee_amount, cur)} />
                <KV label="Admin / Oqood fee" value={`${formatMoney(b.admin_fee_amount, cur, { decimals: 0 })} / ${formatMoney(b.oqood_fee_amount, cur, { decimals: 0 })}`} />
              </>
            )}
            <KV label="Payment plan" value={b.payment_plan?.name || b.payment_plan_name || '—'} />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard icon="account_balance_wallet" title="Collections">
            <KV label="Paid" value={formatMoney(b.amount_paid, cur)} bold color="#1F8A56" />
            <KV label="Outstanding" value={formatMoney(round2((toNumber(b.net_price) || 0) - (toNumber(b.amount_paid) || 0)), cur)} />
            <KV label="Currently due" value={formatMoney(b.amount_due, cur)} color={toNumber(b.amount_due) > 0 ? '#BA1A1A' : null} />
            <MDBox mt={1.5}><ProgressBar label="Paid of net price" value={b.paid_pct} color="#1F8A56" /></MDBox>
            <MDBox mt={1.5}>
              {au ? (
                <>
                  <KV label="Contracts exchanged" value={formatDate(b.contract_exchanged_date)} />
                  <KV label="Cooling-off ends" value={formatDate(b.cooling_off_ends)} />
                  <KV label="Settlement" value={formatDate(b.settlement_date)} />
                </>
              ) : (
                <>
                  <KV label="SPA signed" value={formatDate(b.spa_signed_date)} />
                  <KV label="Oqood" value={b.oqood_no ? `${b.oqood_no} · ${formatDate(b.oqood_registered_date)}` : '—'} />
                  <KV label="Title deed" value={b.title_deed_no || '—'} />
                </>
              )}
            </MDBox>
          </InfoCard>
        </Grid>
      </Grid>

      <InfoCard
        icon="event_note"
        title="Payment schedule"
        headerAction={canEdit && !cancelled && !anyPaid ? (
          <MDButton size="small" variant="text" color="info" onClick={regenerate} disabled={busy}>Regenerate</MDButton>
        ) : null}
      >
        <ReTable
          dense
          rows={installments}
          emptyMessage="No installments."
          rowSx={(i) => (i.status === 'OVERDUE' ? { bgcolor: '#FFF5F4' } : {})}
          columns={[
            { key: 'sequence', label: '#', width: 36 },
            { key: 'label', label: 'Milestone', footer: 'Total' },
            { key: 'due', label: 'Due', render: (i) => (i.due_date ? formatDate(i.due_date) : <MDTypography variant="caption" color="text">{triggerText(i)}</MDTypography>) },
            { key: 'percentage', label: '%', align: 'right', render: (i) => formatPct(i.percentage, 2) },
            { key: 'amount', label: 'Amount', align: 'right', render: (i) => <Num>{formatMoney(i.amount, cur)}</Num>, footer: <Num bold>{formatMoney(scheduleTotals.amount, cur)}</Num> },
            { key: 'amount_paid', label: 'Paid', align: 'right', render: (i) => <Num>{formatMoney(i.amount_paid, cur)}</Num>, footer: <Num bold>{formatMoney(scheduleTotals.paid, cur)}</Num> },
            {
              key: 'outstanding', label: 'Outstanding', align: 'right',
              render: (i) => <Num>{['CANCELLED', 'WAIVED'].includes(i.status) ? '—' : formatMoney(round2((toNumber(i.amount) || 0) - (toNumber(i.amount_paid) || 0)), cur)}</Num>,
              footer: <Num bold>{formatMoney(round2(scheduleTotals.amount - scheduleTotals.paid), cur)}</Num>,
            },
            { key: 'status', label: 'Status', render: (i) => <ReStatusChip vocab="installment" status={i.status} size="xs" /> },
            {
              key: 'actions', label: '', align: 'right',
              render: (i) => {
                const open = !['PAID', 'CANCELLED', 'WAIVED'].includes(i.status);
                const inv = i.invoice_id ? invoiceById[i.invoice_id] : null;
                return (
                  <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                    {i.invoice_id ? (
                      <MDButton size="small" variant="text" color="info" onClick={() => navigate(`/invoices/${i.invoice_id}`)}>
                        {inv?.invoice_number || 'Invoice'}
                      </MDButton>
                    ) : (canEdit && open && !cancelled && (
                      <MDButton size="small" variant="text" color="info" onClick={() => createInvoice(i)} disabled={busy}>Invoice</MDButton>
                    ))}
                    {canEdit && open && !cancelled && (
                      <MDButton size="small" variant="text" color="success" onClick={() => setPayFor(i)}>Pay</MDButton>
                    )}
                  </MDBox>
                );
              },
            },
          ]}
        />
      </InfoCard>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={7}>
          <InfoCard
            icon="handshake"
            title="Commissions"
            headerAction={canEditCommission && !cancelled ? (
              <MDButton size="small" variant="text" color="info" onClick={recalc} disabled={busy}>Recalculate</MDButton>
            ) : null}
          >
            <ReTable
              dense
              rows={b.commissions || []}
              emptyMessage="No commission accrued on this booking."
              columns={[
                { key: 'payee_name', label: 'Payee', render: (c) => c.payee_name || c.broker_firm_name || c.broker_agent_name || humanize(c.payee_type) },
                { key: 'rate_pct', label: 'Rate', align: 'right', render: (c) => (c.rate_pct != null ? formatPct(c.rate_pct, 2) : '—') },
                { key: 'gross_amount', label: 'Gross', align: 'right', render: (c) => <Num>{formatMoney(c.gross_amount, c.currency)}</Num> },
                { key: 'vat_amount', label: 'VAT', align: 'right', render: (c) => <Num>{formatMoney(c.vat_amount, c.currency)}</Num> },
                { key: 'total_amount', label: 'Total', align: 'right', render: (c) => <Num bold>{formatMoney(c.total_amount, c.currency)}</Num> },
                { key: 'status', label: 'Status', render: (c) => <ReStatusChip vocab="commission" status={c.status} size="xs" /> },
              ]}
            />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <Notes booking={b} canEdit={canEdit} onSaved={reload} notify={notify} />
          {resales.items.length > 0 && (
            <MDBox mt={2}>
              <InfoCard icon="swap_horiz" title="Resales / assignments">
                {resales.items.map((r) => (
                  <MDBox key={r.id} mb={1}>
                    <KV label={r.buyer_contact_name || `Buyer #${r.buyer_contact_id}`} value={formatMoney(r.resale_price, cur)} />
                    <MDTypography variant="caption" color="text">
                      {[humanize(r.status) || 'Requested', r.noc_issued_date && `NOC ${formatDate(r.noc_issued_date)}`, r.transfer_date && `transferred ${formatDate(r.transfer_date)}`].filter(Boolean).join(' · ')}
                    </MDTypography>
                  </MDBox>
                ))}
              </InfoCard>
            </MDBox>
          )}
        </Grid>
      </Grid>

      <PaymentDialog
        open={Boolean(payFor)}
        booking={b}
        installment={payFor}
        onClose={() => setPayFor(null)}
        onDone={() => { setPayFor(null); notify('Payment recorded'); reload(); }}
      />
      <CancelDialog open={cancelOpen} booking={b} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); notify('Booking cancelled'); reload(); }} />
      <ResaleDialog open={resaleOpen} booking={b} onClose={() => setResaleOpen(false)} onDone={() => { setResaleOpen(false); notify('Resale recorded'); resales.reload(); }} />
      <RegistrationDialog open={regOpen} booking={b} onClose={() => setRegOpen(false)} onDone={() => { setRegOpen(false); notify('Booking updated'); reload(); }} />
      {snack}
    </RePage>
  );
}
